/**
 * 設定 → 工種・場所・品目（#129 の工種・場所の雛形に #118 で品目を足し、1つの画面のタブにした。docs/47 §2.1・§11、docs/10 §9.1・§9.14）。管理者だけ書ける。
 *   タブ＝URL：/settings/trades（工種）・/settings/places（場所＝よく使う場所の見本。見積へは写して使う）・/settings/items（品目）
 *   ・2段まで（仮設工事 ＞ 足場、2F ＞ 子供部屋）。並べ替えは つまみのドラッグと、上の「並べ替え」で出す ↑↓ の両方（ドラッグだけの操作を作らない＝WCAG 2.2 SC 2.5.7）
 *   ・行は つまみ＋名前＋「使う」のスイッチ＋「⋮」（名前変更／削除。品目は修正／削除）（S-00009、docs/62 §9）
 *   ・「使わない」（スイッチ）：見積の候補に出さない（付いている行の名前には使う）。誰も使っていなければ「削除」で消える
 *   ・見本は「業種見本追加」1つ（#142 段4。会社の業種。業種が無ければ横で選ぶ）。2回押しても増えない（DB の fn_trades_seed・fn_items_seed）
 *   ・場所の雛形は見積に写して使う（見積の場所は見積ごと）。空なら 外部／1F／2F／屋上 を入れられる
 */
import {
  AXES_MSG,
  PHOTO_ASSIGN_MSG,
  axesSettingsTitle,
  INDUSTRY_LABEL,
  INDUSTRY_MSG,
  type Industry,
  WORK_TYPE_LABEL,
  WorkType,
  TAX_CATEGORIES,
  TAX_CATEGORY_LABEL,
  TAX_CATEGORY_SHORT,
  TAX_MSG,
  type TaxCategory,
  type EstimatePlace,
  type Item,
  type ItemList,
  type PartnerList,
  type PlaceList,
  type Trade,
  type TradeList,
} from "@genbaryoku/contract";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router";
import { api, messageOf } from "../api.js";
import { ME_KEY, useMe } from "../auth.js";
import { t } from "../labels.js";
import { hasModule, useModule } from "../core/slots.jsx";
import { useUses, uses } from "../core/uses.js";
import { useBackNav, usePageName } from "../components/BackButton.jsx";
import { treeOptions } from "../components/EstimateAxes.jsx";
import { IndustrySelect } from "../components/IndustrySelect.jsx";
import { GripIcon, GroupList, GroupSection, useFlip, useRowDrag } from "../components/GroupList.jsx";
import { RowMenu, type RowMenuItem } from "../components/RowMenu.jsx";
import { UnitInput } from "../components/UnitInput.jsx";
import { SettingsHead } from "../components/SettingsHead.jsx";
import { ErrorText, Field, Help, Switch, useToast } from "../components/ui.jsx";

type Node = { id: string; parentId: string | null; name: string; sortNo: number; archived: boolean };
type Kind = "trade" | "place";

const PLACE_SAMPLES = ["外部", "1F", "2F", "屋上"];
/** ⋮ の項目（小さいボタンは名詞形。S-00009、docs/10 §5.1.2）。「使う」のスイッチの横の言葉は 2026-10-21 にやめた（つまみだけ＝docs/89 ★15） */
const AXES_RENAME = "名前変更";

function NodeTree({ kind, sorting }: { kind: Kind; sorting: boolean }) {
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const base = kind === "trade" ? "/trades" : "/places";
  const key = kind === "trade" ? ["trades"] : ["places"];
  const word = kind === "trade" ? t(AXES_MSG.trade) : "場所";
  const q = useQuery({ queryKey: key, queryFn: () => api<TradeList | PlaceList>("GET", base) });
  const [name, setName] = useState("");
  const [parentId, setParentId] = useState("");
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null);

  const items: Node[] = (q.data?.items ?? []) as Array<Trade | EstimatePlace>;
  const set = (list: { items: unknown[] }): void => {
    qc.setQueryData(key, { items: list.items });
    void qc.invalidateQueries({ queryKey: kind === "trade" ? ["trades"] : ["places"] });
  };
  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setErr(undefined);
    try {
      await f();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const siblings = (n: Node) => items.filter((x) => (x.parentId ?? null) === (n.parentId ?? null)).sort((a, b) => a.sortNo - b.sortNo || a.name.localeCompare(b.name, "ja"));
  const reorder = (ids: string[]) => run(async () => set(await api<{ items: unknown[] }>("POST", `${base}/reorder`, { ids })));
  const move = (n: Node, d: -1 | 1) => {
    const sib = siblings(n);
    const k = sib.findIndex((x) => x.id === n.id);
    const j = k + d;
    if (j < 0 || j >= sib.length) return;
    const ids = sib.map((x) => x.id);
    [ids[k], ids[j]] = [ids[j]!, ids[k]!];
    void reorder(ids);
  };
  // つまみのドラッグ（同じ段の中だけ。to＝つまみの行を抜いた並びに入れる位置）
  const { drag, rows, rowRef, gripProps } = useRowDrag({
    onDrop: (id, to) => {
      const n = items.find((x) => x.id === id);
      if (!n) return;
      snapshot();
      const ids = siblings(n).map((x) => x.id);
      if (ids.indexOf(id) === to) return;
      const rest = ids.filter((x) => x !== id);
      rest.splice(to, 0, id);
      void reorder(rest);
    },
    onTap: () => undefined,
  });
  const snapshot = useFlip(rows);

  const add = (e: React.FormEvent) => {
    e.preventDefault();
    const v = name.trim();
    if (!v) {
      setErr("名前を入れてください");
      return;
    }
    void run(async () => {
      set(await api<{ items: unknown[] }>("POST", base, { name: v, ...(parentId ? { parentId } : {}) }));
      setName("");
      toast(`${word}「${v}」を追加しました。`);
    });
  };
  const seedPlaces = () =>
    run(async () => {
      let last: { items: unknown[] } | undefined;
      for (const n of PLACE_SAMPLES.filter((x) => !items.some((y) => !y.parentId && y.name === x))) last = await api<{ items: unknown[] }>("POST", "/places", { name: n });
      if (last) set(last);
      toast(AXES_MSG.seeded.replace("{n}", String(PLACE_SAMPLES.length)));
    });

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  // 使わないものも並べる（しるしを付ける）
  const tree = treeOptions(items.map((x) => ({ ...x, archived: false }))).map((t) => ({ ...t, node: items.find((x) => x.id === t.node.id)! }));
  const tops = items.filter((x) => !x.parentId);

  return (
    <>
      {!admin ? <p className="readonly-note">変更できるのは管理者のみです。</p> : null}
      {items.length === 0 && admin ? (
        <div className="paper axseed" data-testid="axis-seed" style={{ padding: "16px 22px", marginBottom: 16, maxWidth: 760 }}>
          <b>{word}がまだありません。</b>
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            {kind === "trade" ? (
              <IndustrySeedButton target="trades" busy={busy} testId="seed-industry" onSeeded={(r) => { set(r); toast(r.message); }} />
            ) : (
              <button type="button" className="btn" disabled={busy} data-testid="seed-places" onClick={() => void seedPlaces()}>
                {AXES_MSG.seed}（{PLACE_SAMPLES.join("・")}）
              </button>
            )}
          </div>
        </div>
      ) : null}

      <GroupSection className="axsec">
        {tree.length === 0 ? (
          <p className="hint axnone">（まだありません）</p>
        ) : (
          <GroupList className="axlist" data-testid="axis-tree">
            {tree.map(({ node: n, depth }) => {
              const sib = siblings(n);
              const k = sib.findIndex((x) => x.id === n.id);
              const target = drag?.target?.id === n.id ? drag.target.where : null;
              return (
                <AxRow
                  key={n.id}
                  name={n.name}
                  depth={depth}
                  off={n.archived}
                  admin={admin}
                  sorting={sorting}
                  busy={busy}
                  canUp={k > 0}
                  canDown={k < sib.length - 1}
                  onMove={(d) => move(n, d)}
                  onToggle={(use) => void run(async () => set(await api<{ items: unknown[] }>("PUT", `${base}/${n.id}`, { archived: !use })))}
                  menu={[
                    { label: AXES_RENAME, onSelect: () => setRenaming({ id: n.id, name: n.name }), testId: "ax-rename" },
                    {
                      label: "削除",
                      danger: true,
                      testId: "ax-delete",
                      onSelect: () =>
                        void run(async () => {
                          const r = await api<{ message: string; items: unknown[] }>("DELETE", `${base}/${n.id}`);
                          set(r);
                          toast(r.message);
                        }),
                    },
                  ]}
                  editing={
                    renaming?.id === n.id ? (
                      <form
                        className="axedit"
                        onSubmit={(e) => {
                          e.preventDefault();
                          const v = renaming.name.trim();
                          if (!v) return;
                          void run(async () => {
                            set(await api<{ items: unknown[] }>("PUT", `${base}/${n.id}`, { name: v }));
                            setRenaming(null);
                          });
                        }}
                      >
                        <input className="inp" autoFocus value={renaming.name} aria-label={`${word}の名前`} onChange={(e) => setRenaming({ id: n.id, name: e.target.value })} onKeyDown={(e) => e.key === "Escape" && setRenaming(null)} />
                        <span className="axedit-btns">
                          <button type="button" className="btn small text" onClick={() => setRenaming(null)}>取消</button>
                          <button type="submit" className="btn small primary" disabled={busy}>名前保存</button>
                        </span>
                      </form>
                    ) : undefined
                  }
                  drag={{ dy: drag?.id === n.id ? drag.dy : 0, dragging: drag?.id === n.id, target }}
                  rowRef={rowRef(n.id)}
                  grip={gripProps(n.id, sib.map((x) => x.id))}
                  testId="axis-node"
                />
              );
            })}
          </GroupList>
        )}
      </GroupSection>

      {admin ? (
        <form className="paper" style={{ maxWidth: 760, padding: "16px 22px", marginBottom: 16 }} onSubmit={add} noValidate>
          <div className="row" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Field id="ax_name" label={`${word}の名前`} style={{ flex: "2 1 220px" }}>
              <input id="ax_name" className="inp" value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field id="ax_parent" label="どの下に" style={{ flex: "1 1 200px" }}>
              <select id="ax_parent" className="inp" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                <option value="">（1段目）</option>
                {tops.filter((x) => !x.archived).map((x) => (
                  <option key={x.id} value={x.id}>{x.name} の下</option>
                ))}
              </select>
            </Field>
            <button type="submit" className="btn primary" disabled={busy} data-testid="axis-add">{word}追加</button>
          </div>
          {kind === "trade" && items.length > 0 ? (
            <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
              <IndustrySeedButton target="trades" busy={busy} small testId="seed-industry" onSeeded={(r) => { set(r); toast(r.message); }} />
            </div>
          ) : null}
          <ErrorText>{err}</ErrorText>
        </form>
      ) : null}
      {toastNode}
    </>
  );
}

type Tab = "trades" | "places" | "items";
const TABS: Array<{ tab: Tab; to: string; label: string }> = [
  { tab: "trades", to: "/settings/trades", label: PHOTO_ASSIGN_MSG.tabTrades },
  { tab: "places", to: "/settings/places", label: PHOTO_ASSIGN_MSG.tabPlaces },
  { tab: "items", to: "/settings/items", label: PHOTO_ASSIGN_MSG.tabItems },
];
/** 「？」の文。{pw:tradeExample} は製品ごとの例（#142 段6 No.23）、品目は field の有無で文を分ける（No.24） */
const HELP: Record<Tab, string> = {
  trades: "見積の行に付けると、表示順「{trade}」「{trade}→場所」で{trade}ごとの内訳書になります。2段まで（{pw:tradeExample}）。見本は2回押しても増えません。「使わない」にした{trade}は見積の候補に出ません。",
  places: "よく使う場所の見本です。見積の行の「場所」やスマホの写真の引き出しで選ぶと、その見積の場所として写します（見積ごとに足し・修正できます。見本を修正しても前の見積は変わりません）。2段まで（2F ＞ 子供部屋）。",
  items: "スマホの写真の引き出しの「品目」に出ます。選ぶと、{trade}・材工区分・既定の単位・いつもの{vendor}が見積の行（仮）に入ります。単価はここでは持ちません。「使わない」にした品目は引き出しに出ません。",
};
/** 写真の引き出し（field）の無い会社の品目の「？」：品目は一覧として持つだけ（見積の候補には出ない） */
const HELP_ITEMS_NO_FIELD = "よく使う品目と、その{trade}・既定の単位・いつもの{vendor}をまとめておく一覧です。単価はここでは持ちません。";

/** 設定 → 工種・場所・品目（タブ）。タブは URL（戻る・リンクで同じタブ） */
function SettingsAxesPage({ tab }: { tab: Tab }) {
  const back = useBackNav({ to: "/settings", label: "設定" });
  const nav = useNavigate();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  // 使う機能（#142 段3）で OFF のタブは出さない（URL で開いた今のタブは出す）。題も出しているタブだけで組む（段6 No.26）
  const can = useUses();
  const title = t(axesSettingsTitle({ trades: can("trades"), places: can("places"), items: can("items") }));
  usePageName(title);
  const tabs = TABS.filter((x) => x.tab === tab || can(x.tab));
  const field = useModule("field");
  const help = tab === "items" && !field ? HELP_ITEMS_NO_FIELD : HELP[tab];
  /** 並べ替え（S-00009）：押すと行の ↑↓ を出す（スイッチと ⋮ の代わり）。タブを移ると戻す */
  const [sorting, setSorting] = useState(false);
  const sortBtn = admin ? (
    <button type="button" className={"btn text cotop-btn" + (sorting ? " cotop-done" : "")} aria-pressed={sorting} onClick={() => setSorting((v) => !v)} data-testid="axis-sort">
      {sorting ? "完了" : "並べ替え"}
    </button>
  ) : null;
  return (
    <div className="axpage" data-testid="axis-page">
      <SettingsHead
        back={back}
        title={
          <>
            {title}
            <Help testId={`help-${tab}`} label={`${t(TABS.find((x) => x.tab === tab)!.label)}の説明`} text={t(help)} />
          </>
        }
        end={sortBtn}
      />
      <div className="seg axtabs" role="tablist" aria-label={title}>
        {tabs.map((x) => (
          <button
            key={x.tab}
            type="button"
            role="tab"
            aria-selected={tab === x.tab}
            aria-pressed={tab === x.tab}
            onClick={() => {
              if (tab === x.tab) return;
              setSorting(false);
              nav(x.to, { replace: true });
            }}
            data-testid={`axtab-${x.tab}`}
          >
            {t(x.label)}
          </button>
        ))}
      </div>
      {tab === "trades" ? <NodeTree key="trade" kind="trade" sorting={sorting} /> : tab === "places" ? <NodeTree key="place" kind="place" sorting={sorting} /> : <ItemTable sorting={sorting} />}
    </div>
  );
}

export const SettingsTradesPage = () => <SettingsAxesPage tab="trades" />;
export const SettingsPlacesPage = () => <SettingsAxesPage tab="places" />;
export const SettingsItemsPage = () => <SettingsAxesPage tab="items" />;

/** taxCategory（T2）：品目の税区分（既定 標準）。候補の引き出し・写真の引き出しで選ぶと明細に写る */
type ItemDraft = { name: string; unit: string; tradeId: string; workType: string; vendorPartnerId: string; taxCategory: TaxCategory };
const emptyDraft: ItemDraft = { name: "", unit: "", tradeId: "", workType: "", vendorPartnerId: "", taxCategory: "standard" };
const draftOf = (x: Item): ItemDraft => ({ name: x.name, unit: x.unit, tradeId: x.tradeId ?? "", workType: x.workType ?? "", vendorPartnerId: x.vendorPartnerId ?? "", taxCategory: x.taxCategory ?? "standard" });
const bodyOf = (d: ItemDraft) => ({ name: d.name.trim(), unit: d.unit.trim(), tradeId: d.tradeId || null, workType: (d.workType || null) as WorkType | null, vendorPartnerId: d.vendorPartnerId || null, taxCategory: d.taxCategory });

/**
 * 品目（#118）：名前・工種・材工区分・既定の単位・いつもの外注先。並べ替えは ↑↓ とドラッグの両方。
 * 使わない＝写真の引き出しに出さない。見積の行が指していれば「消す」は「使わない」になる
 */
function ItemTable({ sorting }: { sorting: boolean }) {
  const qc = useQueryClient();
  const { toast, node: toastNode } = useToast();
  const me = useMe();
  const admin = me.data?.user.role === "admin";
  const field = hasModule(me.data, "field");
  const useTrades = uses(me.data, "trades");
  const q = useQuery({ queryKey: ["items"], queryFn: () => api<ItemList>("GET", "/items") });
  const trades = useQuery({ queryKey: ["trades"], queryFn: () => api<TradeList>("GET", "/trades") });
  const vendors = useQuery({ queryKey: ["partners", "vendor"], queryFn: () => api<PartnerList>("GET", "/partners?type=vendor") });
  const [draft, setDraft] = useState<ItemDraft>(emptyDraft);
  const [edit, setEdit] = useState<{ id: string; d: ItemDraft } | null>(null);
  const [err, setErr] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const items = q.data?.items ?? [];
  const tradeOpts = treeOptions(trades.data?.items ?? []);
  const vendorOpts = (vendors.data?.items ?? []).map((v) => ({ id: v.id, name: v.shortName || v.name }));
  const tradeName = (id: string | null) => (id ? (tradeOpts.find((o) => o.node.id === id)?.label ?? (trades.data?.items ?? []).find((t) => t.id === id)?.name ?? "") : "");
  const vendorName = (id: string | null) => (id ? (vendorOpts.find((v) => v.id === id)?.name ?? "") : "");
  const set = (list: { items: Item[] }): void => {
    qc.setQueryData(["items"], { items: list.items });
  };
  const run = async (f: () => Promise<void>) => {
    setBusy(true);
    setErr(undefined);
    try {
      await f();
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setBusy(false);
    }
  };
  const reorder = (ids: string[]) => run(async () => set(await api<ItemList>("POST", "/items/reorder", { ids })));
  const move = (it: Item, d: -1 | 1) => {
    const k = items.findIndex((x) => x.id === it.id);
    const j = k + d;
    if (j < 0 || j >= items.length) return;
    const ids = items.map((x) => x.id);
    [ids[k], ids[j]] = [ids[j]!, ids[k]!];
    void reorder(ids);
  };
  // つまみのドラッグ（to＝つまみの行を抜いた並びに入れる位置）
  const { drag, rows, rowRef, gripProps } = useRowDrag({
    onDrop: (id, to) => {
      snapshot();
      const ids = items.map((x) => x.id);
      if (ids.indexOf(id) === to) return;
      const rest = ids.filter((x) => x !== id);
      rest.splice(to, 0, id);
      void reorder(rest);
    },
    onTap: () => undefined,
  });
  const snapshot = useFlip(rows);
  const fields = (d: ItemDraft, put: (d: ItemDraft) => void, idp: string) => (
    <>
      <Field id={`${idp}_name`} label={PHOTO_ASSIGN_MSG.itemName} style={{ flex: "2 1 200px" }}>
        <input id={`${idp}_name`} className="inp" value={d.name} maxLength={100} onChange={(e) => put({ ...d, name: e.target.value })} />
      </Field>
      {/* 品目の{trade}は使う機能「{trade}」の会社だけ（#142 段3。値は残す） */}
      {useTrades ? (
        <Field id={`${idp}_trade`} label={t(AXES_MSG.trade)} style={{ flex: "1 1 200px" }}>
          <select id={`${idp}_trade`} className="inp" value={d.tradeId} onChange={(e) => put({ ...d, tradeId: e.target.value })}>
            <option value="">{t(PHOTO_ASSIGN_MSG.noTrade)}</option>
            {tradeOpts.map((o) => (
              <option key={o.node.id} value={o.node.id}>{o.label}</option>
            ))}
          </select>
        </Field>
      ) : null}
      {/* 材工区分はモジュール field の会社だけ（#142 段2。値は残す） */}
      {field ? (
        <Field id={`${idp}_work`} label={PHOTO_ASSIGN_MSG.workType} style={{ flex: "1 1 200px" }}>
          <select id={`${idp}_work`} className="inp" value={d.workType} onChange={(e) => put({ ...d, workType: e.target.value })}>
            <option value="">—</option>
            {WorkType.options.map((w) => (
              <option key={w} value={w}>{WORK_TYPE_LABEL[w]}</option>
            ))}
          </select>
        </Field>
      ) : null}
      <Field id={`${idp}_unit`} label={PHOTO_ASSIGN_MSG.defaultUnit} style={{ flex: "0 1 120px" }}>
        {/* #164：会社の単位の一覧を候補に（明細行の単位の欄と同じ部品・同じく文字で持つ） */}
        <UnitInput id={`${idp}_unit`} value={d.unit} placeholder=" " label={PHOTO_ASSIGN_MSG.defaultUnit} onChange={(v) => put({ ...d, unit: v })} />
      </Field>
      {/* T2：税区分（既定 標準）。区分の名前は業種で変えない */}
      <Field id={`${idp}_tax`} label={TAX_MSG.category} style={{ flex: "1 1 180px" }}>
        <select id={`${idp}_tax`} className="inp" value={d.taxCategory} onChange={(e) => put({ ...d, taxCategory: e.target.value as TaxCategory })} data-testid={`${idp}-tax`}>
          {TAX_CATEGORIES.map((c) => (
            <option key={c} value={c}>{TAX_CATEGORY_LABEL[c]}</option>
          ))}
        </select>
      </Field>
      <Field id={`${idp}_vendor`} label={t(PHOTO_ASSIGN_MSG.vendor)} style={{ flex: "1 1 200px" }}>
        <select id={`${idp}_vendor`} className="inp" value={d.vendorPartnerId} onChange={(e) => put({ ...d, vendorPartnerId: e.target.value })}>
          <option value="">{PHOTO_ASSIGN_MSG.noVendor}</option>
          {vendorOpts.map((v) => (
            <option key={v.id} value={v.id}>{v.name}</option>
          ))}
        </select>
      </Field>
    </>
  );

  if (q.isError) return <ErrorText>{messageOf(q.error)}</ErrorText>;
  if (!q.data) return <p className="hint">読み込んでいます…</p>;
  return (
    <>
      {!admin ? <p className="readonly-note">変更できるのは管理者のみです。</p> : null}
      {items.length === 0 && admin ? (
        <div className="paper axseed" data-testid="item-seed" style={{ padding: "16px 22px", marginBottom: 16, maxWidth: 960 }}>
          <b>{PHOTO_ASSIGN_MSG.item}がまだありません。</b>
          <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
            <IndustrySeedButton target="items" busy={busy} testId="item-seed-industry" onSeeded={(r) => { set(r as { message: string; items: Item[] }); toast(r.message); }} />
          </div>
        </div>
      ) : null}
      <GroupSection className="axsec">
        {items.length === 0 ? (
          <p className="hint axnone">（まだありません）</p>
        ) : (
          <GroupList className="axlist" data-testid="item-list">
            {items.map((it, k) => {
              const target = drag?.target?.id === it.id ? drag.target.where : null;
              // T2：標準でない税区分は短い名前を添える（軽減・非課税・立替 など）
              const attrs = [useTrades ? tradeName(it.tradeId) : "", it.workType && field ? WORK_TYPE_LABEL[it.workType] : "", it.unit, vendorName(it.vendorPartnerId), it.taxCategory && it.taxCategory !== "standard" ? TAX_CATEGORY_SHORT[it.taxCategory] : ""].filter(Boolean).join("・");
              return (
                <AxRow
                  key={it.id}
                  name={it.name}
                  sub={attrs}
                  off={it.archived}
                  admin={admin}
                  sorting={sorting}
                  busy={busy}
                  canUp={k > 0}
                  canDown={k < items.length - 1}
                  onMove={(d) => move(it, d)}
                  onToggle={(use) => void run(async () => set(await api<ItemList>("PUT", `/items/${it.id}`, { archived: !use })))}
                  menu={[
                    { label: "修正", onSelect: () => setEdit({ id: it.id, d: draftOf(it) }), testId: "item-edit" },
                    {
                      label: "削除",
                      danger: true,
                      testId: "item-delete",
                      onSelect: () =>
                        void run(async () => {
                          const r = await api<{ message: string; items: Item[] }>("DELETE", `/items/${it.id}`);
                          set(r);
                          toast(r.message);
                        }),
                    },
                  ]}
                  editing={
                    edit?.id === it.id ? (
                      <form
                        className="axedit axedit-item"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (!edit.d.name.trim()) return;
                          void run(async () => {
                            set(await api<ItemList>("PUT", `/items/${it.id}`, bodyOf(edit.d)));
                            setEdit(null);
                            toast(PHOTO_ASSIGN_MSG.itemSaved);
                          });
                        }}
                      >
                        {fields(edit.d, (d) => setEdit({ id: it.id, d }), "ie")}
                        <span className="axedit-btns">
                          <button type="button" className="btn small text" onClick={() => setEdit(null)}>取消</button>
                          <button type="submit" className="btn small primary" disabled={busy} data-testid="item-save">{PHOTO_ASSIGN_MSG.saveItem}</button>
                        </span>
                      </form>
                    ) : undefined
                  }
                  drag={{ dy: drag?.id === it.id ? drag.dy : 0, dragging: drag?.id === it.id, target }}
                  rowRef={rowRef(it.id)}
                  grip={gripProps(it.id, items.map((x) => x.id))}
                  testId="item-row"
                  attrsTestId="item-attrs"
                />
              );
            })}
          </GroupList>
        )}
      </GroupSection>
      {admin ? (
        <form
          className="paper"
          style={{ maxWidth: 960, padding: "16px 22px", marginBottom: 16 }}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (!draft.name.trim()) {
              setErr("名前を入れてください");
              return;
            }
            void run(async () => {
              const r = await api<{ item: Item; items: Item[] }>("POST", "/items", bodyOf(draft));
              set(r);
              toast(PHOTO_ASSIGN_MSG.itemAdded.replace("{name}", r.item.name));
              setDraft(emptyDraft);
            });
          }}
        >
          <div className="row" style={{ gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
            {fields(draft, setDraft, "in")}
            <button type="submit" className="btn primary" disabled={busy} data-testid="item-add">{PHOTO_ASSIGN_MSG.addItemSubmit}</button>
          </div>
          {items.length > 0 ? (
            <div className="row" style={{ gap: 8, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
              <IndustrySeedButton target="items" busy={busy} small testId="item-seed-industry" onSeeded={(r) => { set(r as { message: string; items: Item[] }); toast(r.message); }} />
            </div>
          ) : null}
          <ErrorText>{err}</ErrorText>
        </form>
      ) : null}
      {toastNode}
    </>
  );
}

/**
 * 見本のボタン（#142 段4、Q7：工務店・電気・塗装・一般の4つ →「業種見本追加」1つ）。会社の業種の分類／品目の見本を入れる（2回押しても増えない）。
 * 業種が無い会社（今ある会社）は、横の欄で業種を選ぶと押せる（選んでも見本は入らない＝このボタンで入れる）
 */
function IndustrySeedButton({ target, busy, small = false, testId, onSeeded }: { target: "trades" | "items"; busy: boolean; small?: boolean; testId: string; onSeeded: (r: { message: string; items: unknown[] }) => void }) {
  const me = useMe();
  const qc = useQueryClient();
  const product = me.data?.tenant.product ?? "genba";
  const industry = me.data?.tenant.industry ?? null;
  const [err, setErr] = useState<string | undefined>();
  const [working, setWorking] = useState(false);
  const pick = async (v: Industry) => {
    setErr(undefined);
    try {
      await api("PUT", "/company/industry", { industry: v, seed: false });
      await qc.invalidateQueries({ queryKey: ME_KEY });
    } catch (e) {
      setErr(messageOf(e));
    }
  };
  const seed = async () => {
    setErr(undefined);
    setWorking(true);
    try {
      onSeeded(await api<{ message: string; items: unknown[] }>("POST", `/${target}/seed`, {}));
    } catch (e) {
      setErr(messageOf(e));
    } finally {
      setWorking(false);
    }
  };
  return (
    <span className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      {industry ? null : (
        <>
          <IndustrySelect id={`${testId}-pick`} product={product} value={null} allowEmpty onChange={(v) => void pick(v)} />
          <span className="hint">{INDUSTRY_MSG.needIndustry}</span>
        </>
      )}
      <button type="button" className={"btn" + (small ? " small" : "")} disabled={busy || working || !industry} data-testid={testId} onClick={() => void seed()}>
        {INDUSTRY_MSG.seed}
        {industry ? `（${INDUSTRY_LABEL[industry]}）` : ""}
      </button>
      <ErrorText>{err}</ErrorText>
    </span>
  );
}

/**
 * 分類・場所・品目の行（S-00009、docs/62 §9・docs/10 §9.20）：つまみ＋名前（品目は下に属性）＋「使う」のスイッチ＋「⋮」（名前変更／削除。品目は修正／削除）。
 *   ・つまみを押さえて上下に動かす（Pointer Events＝指でもマウスでも。同じ段の中だけ）
 *   ・↑↓ は上の「並べ替え」を押したときだけ（スイッチと ⋮ の代わりに出す）＝ドラッグだけの操作にしない（WCAG 2.2 SC 2.5.7）
 *   ・スイッチは押した瞬間に保存（使わない＝見積の候補・写真の引き出しに出さない。付いている行の名前には使う）
 */
function AxRow({
  name,
  sub,
  depth = 0,
  off,
  admin,
  sorting,
  busy,
  canUp,
  canDown,
  onMove,
  onToggle,
  menu,
  editing,
  drag,
  rowRef,
  grip,
  testId,
  attrsTestId,
}: {
  name: string;
  sub?: string | undefined;
  depth?: number;
  off: boolean;
  admin: boolean;
  sorting: boolean;
  busy: boolean;
  canUp: boolean;
  canDown: boolean;
  onMove: (d: -1 | 1) => void;
  onToggle: (use: boolean) => void;
  menu: RowMenuItem[];
  /** 名前の修正中（行の中に欄とボタン） */
  editing?: ReactNode;
  drag: { dy: number; dragging: boolean; target: "above" | "below" | null };
  rowRef: (el: HTMLLIElement | null) => void;
  grip: Record<string, unknown>;
  testId: string;
  attrsTestId?: string;
}) {
  return (
    <li
      ref={rowRef}
      className={["grow grow-set grow-ax", depth ? "child is-child" : "", off ? "archived is-off" : "", drag.dragging ? "is-dragging" : "", drag.target === "above" ? "is-dropabove" : drag.target === "below" ? "is-dropbelow" : "", editing ? "is-editing" : ""].filter(Boolean).join(" ")}
      style={drag.dragging ? { transform: `translateY(${drag.dy}px)` } : undefined}
      data-testid={testId}
    >
      <div className="growin">
        {admin && !editing ? (
          <span className="axgrip" aria-hidden="true" title={`${name}をドラッグで並べ替え`} {...grip}>
            <GripIcon />
          </span>
        ) : null}
        {editing ?? (
          <>
            <span className="gtx">
              <span className="gtt nm">{name}</span>
              {sub ? (
                <span className="gsb itemattrs" data-testid={attrsTestId}>
                  {sub}
                </span>
              ) : null}
            </span>
            {admin && sorting ? (
              <span className="axmv">
                <button type="button" className="axmvbtn" aria-label={`${name}を上へ`} disabled={busy || !canUp} onClick={() => onMove(-1)}>
                  ↑
                </button>
                <button type="button" className="axmvbtn" aria-label={`${name}を下へ`} disabled={busy || !canDown} onClick={() => onMove(1)}>
                  ↓
                </button>
              </span>
            ) : (
              <>
                <span className="axsw">
                  <Switch checked={!off} onChange={onToggle} label={`${name}を使う`} disabled={!admin || busy} testId={`${testId}-use`} bare />
                </span>
                {admin ? <RowMenu label={`${name}のメニュー`} items={menu} testId={`${testId}-menu`} /> : null}
              </>
            )}
          </>
        )}
      </div>
    </li>
  );
}
